import React from 'react';
import { Box, Typography, Button, TextField, Stack, Paper, Divider, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { urlImg, textoDuracion } from '../common/helpers';
import { dinero } from '../../utils/dinero';
import { MINIMO_COBRO } from '../../utils/reglasPago';

// Con qué se va a pagar (antes decía «Tarjeta guardada» con todo, también con SPEI u OXXO)
const ETIQUETA_PAGO = { card: 'Tarjeta', spei: 'Transferencia SPEI', oxxo: 'Efectivo en OXXO' };

const SummaryItem = ({ icon: Icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
    <Box sx={{ 
      color: 'primary.main', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      '& svg': { fontSize: 20 }
    }}>
      <Icon />
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500, mb: -0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="bold" color="primary.main">
        {value}
      </Typography>
    </Box>
  </Stack>
);

const BookingSummary = ({
  service, profile, ownerUser,
  selectedDate, selectedTime,
  selectedLanguage, selectedModality,
  paymentMethod,
  quote = null,
  onConfirm,
  isConfirming = false,
  // false: no hay con qué pagar (la web sin la llave de Stripe) y el botón no se puede usar
  puedeReservar = true,
  promoCodigo = '', setPromoCodigo = () => {},
  promoAplicado = null, promoError = '', promoLoading = false,
  onAplicarPromo = () => {}, onQuitarPromo = () => {}
}) => {
  const { nombre, costoServicio, img, duracionEstimada, unidadDuracion } = service;

  const finalCost = quote ? quote.costo : costoServicio;
  const subtotal = Number(finalCost) || 0;
  // Descuento del código según su tipo (porcentaje / monto fijo), respetando la compra mínima.
  const promoMin = promoAplicado?.montoMinimo || 0;
  const promoMinNoCumple = !!promoAplicado && subtotal < promoMin;
  const descuentoMonto = (!promoAplicado || promoMinNoCumple) ? 0 : Math.min(
    promoAplicado.tipo === 'monto_fijo'
      ? (promoAplicado.montoFijo || 0)
      : Math.round(subtotal * (promoAplicado.descuentoPct || 0)) / 100,
    // Un código nunca deja el total por debajo de lo mínimo que se cobra (como en el servidor): nada es gratis
    Math.max(0, subtotal - MINIMO_COBRO)
  );
  const total = Math.round((subtotal - descuentoMonto) * 100) / 100;
  const promoLabel = promoAplicado
    ? (promoAplicado.tipo === 'monto_fijo'
        ? `-${dinero(promoAplicado.montoFijo)}`
        : `-${promoAplicado.descuentoPct || 0}%`)
    : '';
  const finalName = quote ? `Cotización: ${quote.concepto}` : nombre;
  // Sin duración registrada no se muestra nada (solo el proveedor)
  const detalleServicio = quote ? 'Proyecto personalizado' : textoDuracion(duracionEstimada, unidadDuracion);
  
  const serviceImg = img && img.length > 0 
    ? `${urlImg}/servicios/${service._id}/${img[0].nombre}` 
    : '/search.png';

  const authorName = ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : (profile?.nombre || 'Proveedor');

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const months = [
      "enero", "febrero", "marzo", "abril", "mayo", "junio",
      "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
    ];
    return `${d.getDate()} de ${months[d.getMonth()]}`;
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        borderRadius: '24px', 
        bgcolor: (t) => alpha(t.palette.secondary.main, 0.1), 
        position: 'sticky', 
        top: 100,
        border: 1, borderColor: (t) => alpha(t.palette.secondary.main, 0.2)
      }}
    >
      <Typography variant="h5" fontWeight="900" color="primary.main" sx={{ mb: 4 }}>
        Resumen
      </Typography>

      <Box sx={{ bgcolor: 'transparent', mb: 4 }}>
        <Box 
          component="img"
          src={serviceImg}
          sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: '16px', mb: 2 }}
        />
        <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 0.5, lineHeight: 1.2 }}>
          {finalName}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {[detalleServicio, authorName].filter(Boolean).join(' • ')}
        </Typography>
      </Box>

      <Divider sx={{ mb: 4, borderColor: 'divider', opacity: 0.6 }} />

      <Box sx={{ mb: 4 }}>
        <SummaryItem icon={CalendarTodayIcon} label="Fecha" value={formatDate(selectedDate)} />
        <SummaryItem icon={AccessTimeIcon} label="Hora" value={selectedTime ? `${selectedTime} hrs` : 'Por seleccionar'} />
        <SummaryItem icon={LanguageIcon} label="Idioma" value={selectedLanguage} />
        <SummaryItem icon={LocationOnIcon} label="Modalidad" value={selectedModality} />
        <SummaryItem icon={CreditCardIcon} label="Pago" value={ETIQUETA_PAGO[paymentMethod] || ETIQUETA_PAGO.card} />
      </Box>

      <Divider sx={{ mb: 4, borderColor: 'divider', opacity: 0.6 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Código promocional
        </Typography>
        {promoAplicado ? (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: (t) => alpha(t.palette.success.main, 0.2), border: 1, borderColor: 'success.main', borderRadius: '12px', px: 2, py: 1.2
          }}>
            <Box>
              <Typography variant="body2" fontWeight={900} color="success.oscuro">{promoAplicado.codigo}</Typography>
              <Typography variant="caption" color={promoMinNoCumple ? 'error.oscuro' : 'success.oscuro'}>
                {promoMinNoCumple ? `Requiere compra mínima de ${dinero(promoMin)}` : `${promoLabel} aplicado`}
              </Typography>
            </Box>
            <Button onClick={onQuitarPromo} size="small" sx={{ textTransform: 'none', color: 'success.oscuro', fontWeight: 700 }}>
              Quitar
            </Button>
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Ej. ONESERVICE20"
                size="small"
                fullWidth
                value={promoCodigo}
                onChange={(e) => setPromoCodigo(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAplicarPromo(); } }}
                error={!!promoError}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': { borderColor: 'divider' }
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={onAplicarPromo}
                disabled={promoLoading || !promoCodigo.trim()}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '900',
                  borderRadius: '12px',
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: 'grey.400', color: 'white' }
                }}
              >
                {promoLoading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Aplicar'}
              </Button>
            </Stack>
            {promoError && (
              <Typography variant="caption" color="error.oscuro" sx={{ mt: 0.75, display: 'block', fontWeight: 600 }}>
                {promoError}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Stack spacing={1} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>Subtotal</Typography>
          <Typography variant="body2" fontWeight="bold" color="primary.main">{dinero(subtotal)}</Typography>
        </Box>
        {descuentoMonto > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="success.oscuro" fontWeight={600}>
              Descuento ({promoAplicado.codigo} {promoLabel})
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="success.oscuro">-{dinero(descuentoMonto)}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="h5" fontWeight="900" color="primary.main">Total</Typography>
          <Typography variant="h5" fontWeight="900" color="primary.main">{dinero(total)}</Typography>
        </Box>
      </Stack>

      <Button
        variant="contained"
        fullWidth
        onClick={onConfirm}
        disabled={isConfirming || !puedeReservar}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 2,
          textTransform: 'none',
          fontWeight: '900',
          borderRadius: '14px',
          fontSize: '16px',
          mb: 3,
          boxShadow: (t) => `0px 8px 16px ${alpha(t.palette.primary.main, 0.2)}`,
          '&:hover': { bgcolor: 'primary.dark', boxShadow: (t) => `0px 12px 20px ${alpha(t.palette.primary.main, 0.3)}` },
          '&.Mui-disabled': { bgcolor: 'text.secondary', color: 'white' }
        }}
      >
        {isConfirming ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Confirmar reserva'}
      </Button>

      <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ px: 2, lineHeight: 1.4, fontSize: '11px' }}>
        Al confirmar aceptas los términos y condiciones del servicio
      </Typography>
    </Paper>
  );
};

export default BookingSummary;
